/**
 * The capsule's cover — the first screen of the studio.
 *
 * It used to *be* the video editor: a slide whose body was a big upload target,
 * with the title and the description pinned underneath it. That is a form, and it
 * asks the creator to fill in a slide before they can see the capsule. A cover is
 * the one screen where the creator should be looking at their own work, so this is
 * the book studio's cover step wearing the capsule's own rules:
 *
 *  - **Before there is art**, the stage is the invitation — what a cover can be,
 *    then the one button that starts it. Nothing else on screen is a question, so
 *    there is nothing to answer out of order.
 *  - **After there is art**, the film is the screen, and only the words sit on it:
 *    the title and the description, exactly where a learner meets them, over the
 *    scrims that keep them readable on any frame.
 *  - **The film's minute is the creator's to choose.** A capsule cover may run for
 *    three minutes and a film longer than that is not refused — it opens the
 *    trimmer, so the window the capsule opens on is picked here rather than by
 *    cutting the film somewhere else and coming back. The window length is the
 *    capsule's (see `#/lib/coverTrim`), which is the whole reason that arithmetic
 *    takes it as a parameter.
 *  - **`Change` and `Trim` are separate questions** — *a different film* and *this
 *    film, a different minute* — which is why they are two controls and not one.
 *
 * The film arrives **silent**. The stage is a picture; sound is the creator's
 * choice, and the trimmer is the one place that makes a noise.
 */
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  type TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import * as ImagePicker from 'expo-image-picker'
import {type ImagePickerAsset} from 'expo-image-picker'
import {LinearGradient} from 'expo-linear-gradient'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {
  CAPSULE_COVER_MAX_MS,
  CAPSULE_COVER_MAX_SECONDS,
  coverNeedsTrim,
} from '#/lib/coverTrim'
import {FIXTURE_COVER_FILM_URI} from '#/lib/flashbookFixtures'
import {StudioCoverTrimSheet} from '#/components/studio/StudioCoverTrimSheet'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {StudioWindowVideo} from '#/components/studio/StudioWindowVideo'
import {type StudioChrome} from '#/themes-studio/chrome'
import {OVER_ART} from '#/themes-studio/overArt'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {type Slide} from '#/types/capsule'

export type CapsuleCoverStageHandle = {
  /** Puts the caret in the title, so the studio header's title button works here. */
  beginTitleEdit: () => void
}

/** A picked film: the URI, and the length that says whether it needs a window. */
export type CapsuleCoverFilm = {uri: string; durationMs?: number}

export function CapsuleCoverStage({
  slide,
  active,
  onChange,
  onVideoAssetSelected,
  onContinue,
  seededFilmMs,
  ref,
}: {
  slide: Slide
  /**
   * Whether the studio is the screen in front of the creator.
   *
   * A stack keeps the screen it came from mounted, so leaving the studio does not
   * unmount the cover — and the film was still playing to an empty room, and still
   * playing when the creator came back. Nothing nobody is looking at should run,
   * so this gates the only thing here that makes a sound (it is muted, but it is
   * still decoding frames).
   */
  active: boolean
  onChange: (data: Partial<Slide>) => void
  /** The picked file, which publishing uploads as the post's video. */
  onVideoAssetSelected: (asset: ImagePickerAsset) => void
  onContinue: () => void
  /**
   * Test-only: the length of a film the picker would have returned (see
   * `FlashBookFixtures`).
   *
   * A browser test cannot hand an `<input type="file">` a real three-minute film,
   * and the length comes from the element's metadata rather than the file's name,
   * so the trimmer would be unreachable from a suite without this. It goes through
   * the same door a real pick goes through, so what the browser measures is the
   * shipping path.
   */
  seededFilmMs?: number
  ref?: React.Ref<CapsuleCoverStageHandle>
}) {
  const cs = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()
  const [picking, setPicking] = useState(false)
  const [permError, setPermError] = useState(false)
  /** The film whose minute is being chosen. Nothing is committed until then. */
  const [trimFilm, setTrimFilm] = useState<CapsuleCoverFilm | null>(null)
  /**
   * The file behind a film that is still being decided on.
   *
   * Publishing uploads an `ImagePickerAsset` — it needs the frame size for the
   * embed's aspect ratio, which a URI does not carry — so the asset has to survive
   * the trimmer. Held here rather than handed over at pick time: a creator who
   * opens the trimmer and cancels has chosen nothing, and a cover with a film
   * queued for upload and none on screen is a capsule that would publish a video
   * nobody picked.
   */
  const trimAsset = useRef<ImagePickerAsset | null>(null)
  const titleRef = useRef<TextInput>(null)
  /**
   * The caret request from the header, held until the title field exists.
   *
   * The header's title button jumps to the cover *and* asks for the caret in one
   * press, but the press can land before this stage has mounted its field — so the
   * request is stored and the effect below serves it, instead of the focus being
   * dropped on the floor.
   */
  const pendingTitleFocus = useRef(false)

  useImperativeHandle(ref, () => ({
    beginTitleEdit: () => {
      pendingTitleFocus.current = true
      titleRef.current?.focus()
    },
  }))

  useEffect(() => {
    if (!pendingTitleFocus.current) return
    pendingTitleFocus.current = false
    titleRef.current?.focus()
  })

  const uri = slide.videoUrl
  const hasArt = !!uri
  const canTrim = hasArt && coverNeedsTrim(slide.videoDurationMs, CAPSULE_COVER_MAX_MS)

  /**
   * The one door a film reaches the cover through.
   *
   * There are two ways in — a short film straight from the picker, a long one back
   * from the trimmer — and they must not commit the cover by different means: the
   * *file* publishing uploads and the *window* the capsule opens on are one
   * decision, so they are applied together and neither can be present while the
   * other is missing.
   */
  const commitFilm = useCallback(
    (
      film: CapsuleCoverFilm,
      asset: ImagePickerAsset | null,
      startMs: number | undefined,
    ) => {
      if (asset) onVideoAssetSelected(asset)
      onChange({
        videoUrl: film.uri,
        videoDurationMs: film.durationMs,
        videoStartMs: startMs,
      })
    },
    [onChange, onVideoAssetSelected],
  )

  /**
   * A picked film, committed — or handed to the trimmer when it runs over.
   *
   * Cancelling the trimmer leaves the cover exactly as it was, because a creator
   * who closes a question has answered nothing.
   */
  const applyFilm = useCallback(
    (film: CapsuleCoverFilm, asset: ImagePickerAsset | null) => {
      if (coverNeedsTrim(film.durationMs, CAPSULE_COVER_MAX_MS)) {
        trimAsset.current = asset
        setTrimFilm(film)
        return
      }
      commitFilm(film, asset, undefined)
    },
    [commitFilm],
  )

  /**
   * One action, whatever the creator has.
   *
   * There is no mode to pick first: a capsule cover *is* a film — it becomes the
   * post's video — so the picker opens on films and what comes back is applied.
   * A film longer than a cover is not refused either; it opens the trimmer.
   */
  const pickFilm = useCallback(async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync()
    if (!perm.granted) {
      setPermError(true)
      return
    }
    setPicking(true)
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['videos'] as ImagePicker.MediaType[],
        // Asked for far more than a cover may run, on purpose: the pick must not
        // be cut at the cap, because choosing the window is this screen's job.
        videoMaxDuration: 600,
        quality: 1,
      })
      const asset = result.canceled ? undefined : result.assets[0]
      if (!asset) return
      setPermError(false)
      applyFilm({uri: asset.uri, durationMs: asset.duration ?? undefined}, asset)
    } finally {
      setPicking(false)
    }
  }, [applyFilm])

  /**
   * The newest `applyFilm`, for the seed below to reach without depending on it.
   *
   * The seed has to run *once*: a creator who picks a minute and applies it must
   * not be asked again. `applyFilm` is a `useCallback` whose identity follows the
   * parent's props — the parent rebuilds its `onChange` every render — so an effect
   * depending on it would re-open the trimmer the moment it was closed, over and
   * over. Reading the newest one through a ref keeps the effect keyed on the film's
   * length alone, which cannot re-render into a different value.
   */
  const applyFilmRef = useRef(applyFilm)
  useEffect(() => {
    applyFilmRef.current = applyFilm
  })

  /**
   * The fixture's film, through the door a real pick goes through.
   *
   * The length is a number rather than a film object for the same reason: an object
   * built in the parent's render is a new value every render.
   */
  useEffect(() => {
    if (!seededFilmMs) return
    applyFilmRef.current(
      {uri: FIXTURE_COVER_FILM_URI, durationMs: seededFilmMs},
      // A fixture has no file behind it, so publishing has nothing to upload —
      // exactly as a book's fixture film behaves.
      null,
    )
  }, [seededFilmMs])

  const canContinue = hasArt

  return (
    <View style={cs.stage}>
      {hasArt ? (
        <>
          <StudioWindowVideo
            uri={uri}
            startMs={slide.videoStartMs}
            windowMs={CAPSULE_COVER_MAX_MS}
            playing={active && !trimFilm}
            muted
            showControls={false}
            style={StyleSheet.absoluteFill}
          />

          {/* Scrims: keep the overlaid words readable on any frame. */}
          <LinearGradient
            pointerEvents="none"
            colors={[OVER_ART.scrimFrom, OVER_ART.scrimClear]}
            style={cs.topScrim}
          />
          <LinearGradient
            pointerEvents="none"
            colors={OVER_ART.titleScrim}
            locations={[0, 0.45, 1]}
            style={cs.bottomScrim}
          />
        </>
      ) : (
        /*
         * Nothing picked yet, so the stage is the invitation: what a capsule cover
         * can be, then the one button that starts it. The two paragraphs are named
         * before the creator is asked to choose, because the questions left — how
         * long may it run, and what happens if it runs longer — are ones they would
         * otherwise only meet as a refusal.
         */
        <View style={cs.empty} testID="capsuleCoverEmpty">
          <View style={cs.emptyIcon}>
            <Feather name="video" size={26} color={c.accent} />
          </View>
          <Text style={cs.emptyTitle}>{l`Add a cover`}</Text>
          <Text style={cs.emptyTxt}>
            {l`A video, and the words a learner reads first. It may run for ${CAPSULE_COVER_MAX_SECONDS} seconds — a longer film is fine, you choose which part plays.`}
          </Text>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Choose a cover video`}
            accessibilityHint={l`Picks a video from your library`}
            disabled={picking}
            onPress={() => void pickFilm()}
            testID="capsuleCoverAddBtn"
            style={[cs.addBtn, {backgroundColor: c.accentFill}]}>
            {picking ? (
              <ActivityIndicator size="small" color={c.onAccent} />
            ) : (
              <Feather name="upload" size={14} color={c.onAccent} />
            )}
            <Text style={[cs.addBtnTxt, {color: c.onAccent}]}>
              {l`Choose a video`}
            </Text>
          </TouchableOpacity>
          {permError && (
            <View style={cs.permError}>
              <Text style={cs.permErrorTxt}>
                {l`Permission required — allow media access in device settings.`}
              </Text>
            </View>
          )}
        </View>
      )}

      {/*
        The two controls over the art, and the reason there are two: *this film, a
        different minute* and *a different film* are different questions. A creator
        who liked what they shot but chose the wrong opening should not have to find
        the file again in their library.
      */}
      {hasArt && (
        <View style={[cs.top, cs.topRow]} pointerEvents="box-none">
          {canTrim && (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Choose the minute the cover opens on`}
              accessibilityHint={l`Opens the trimmer for this film`}
              onPress={() =>
                setTrimFilm({
                  uri: uri,
                  durationMs: slide.videoDurationMs,
                })
              }
              testID="capsuleCoverTrimBtn"
              style={cs.tab}>
              <Feather name="scissors" size={13} color={OVER_ART.inkStrong} />
              <Text style={[cs.tabTxt, {color: OVER_ART.inkStrong}]}>
                {slide.videoStartMs != null ? l`Trimmed` : l`Trim`}
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Change the cover video`}
            accessibilityHint={l`Picks another video from your library`}
            disabled={picking}
            onPress={() => void pickFilm()}
            testID="capsuleCoverChangeBtn"
            style={cs.tab}>
            {picking ? (
              <ActivityIndicator size="small" color={OVER_ART.inkStrong} />
            ) : (
              <Feather name="refresh-cw" size={13} color={OVER_ART.inkStrong} />
            )}
            <Text style={[cs.tabTxt, {color: OVER_ART.inkStrong}]}>
              {l`Change`}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Title & description sit on the cover, exactly as learners will see them. */}
      <View style={cs.bottom}>
        <StudioTextField
          ref={titleRef}
          testID="capsuleCoverTitle"
          accessibilityLabel={l`Capsule title`}
          accessibilityHint={l`The name learners will see`}
          style={[cs.titleInput, hasArt && cs.titleOnArt]}
          value={slide.contentTitle ?? ''}
          onChangeText={contentTitle => onChange({contentTitle})}
          placeholder={l`Capsule title…`}
          placeholderTextColor={hasArt ? OVER_ART.inkFaint : c.textFaint}
          textAlignVertical="top"
          maxHeight={96}
        />
        <StudioTextField
          testID="capsuleCoverDesc"
          accessibilityLabel={l`Capsule description`}
          accessibilityHint={l`A short introduction to the capsule`}
          style={[cs.descInput, hasArt && cs.descOnArt]}
          value={slide.contentText ?? ''}
          onChangeText={contentText => onChange({contentText})}
          placeholder={l`Description or intro…`}
          placeholderTextColor={hasArt ? OVER_ART.inkFaint : c.textFaint}
          minLines={2}
          textAlignVertical="top"
          maxHeight={72}
        />

        {/* Why Continue is not available yet, named for what is missing. */}
        {!canContinue && (
          <Text style={[cs.reqTxt, !hasArt && {color: c.textMuted}]}>
            {l`Add a cover video to continue`}
          </Text>
        )}

        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Continue`}
          accessibilityHint={
            canContinue
              ? l`Goes on to the capsule's slides`
              : l`Add a cover video first`
          }
          disabled={!canContinue}
          onPress={onContinue}
          testID="capsuleCoverContinueBtn"
          style={[
            cs.continueBtn,
            {backgroundColor: c.accentFill, opacity: canContinue ? 1 : 0.5},
          ]}>
          <Text style={[cs.continueTxt, {color: c.onAccent}]}>{l`Continue`}</Text>
          <Feather name="arrow-right" size={18} color={c.onAccent} />
        </TouchableOpacity>
      </View>

      {/*
        The trimmer, while a film's minute is being chosen.

        Mounted only while there is a candidate, so its state is fresh each time it
        opens and the studio's own cover stays untouched until it is answered.
      */}
      {trimFilm && (
        <StudioCoverTrimSheet
          film={trimFilm}
          startMs={slide.videoStartMs ?? 0}
          accent={c.accentFill}
          windowSeconds={CAPSULE_COVER_MAX_SECONDS}
          onApply={startMs => {
            commitFilm(trimFilm, trimAsset.current, startMs)
            trimAsset.current = null
            setTrimFilm(null)
          }}
          onCancel={() => {
            // Nothing was chosen, so nothing is owed to publishing either.
            trimAsset.current = null
            setTrimFilm(null)
          }}
        />
      )}
    </View>
  )
}

const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    // The stage is studio furniture while it is empty, and the film once there is
    // one — the film's own matte, because nothing else should show through it.
    stage: {flex: 1, backgroundColor: c.stage},
    empty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      paddingHorizontal: 28,
      backgroundColor: c.surface,
    },
    emptyIcon: {
      width: 64,
      height: 64,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    emptyTitle: {
      color: c.text,
      fontSize: 21,
      fontWeight: '800',
      textAlign: 'center',
    },
    emptyTxt: {
      color: c.textMuted,
      fontSize: 13,
      lineHeight: 19,
      fontWeight: '600',
      textAlign: 'center',
      maxWidth: 320,
    },
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderRadius: 999,
      paddingHorizontal: 20,
      paddingVertical: 13,
      marginTop: 4,
    },
    addBtnTxt: {fontSize: 14, fontWeight: '800'},
    permError: {
      backgroundColor: c.dangerTint,
      borderRadius: 10,
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderWidth: 1,
      borderColor: c.dangerBorder,
      marginTop: 6,
    },
    permErrorTxt: {color: c.danger, fontSize: 12, textAlign: 'center'},
    topScrim: {position: 'absolute', top: 0, left: 0, right: 0, height: 160},
    bottomScrim: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 340,
    },
    top: {position: 'absolute', top: 0, left: 0, right: 0, padding: 12},
    topRow: {flexDirection: 'row', justifyContent: 'flex-end', gap: 8},
    /**
     * The pills over the creator's own film. The film cannot be assumed light or
     * dark, so over art they go translucent black with white ink — which reads on
     * a bright sky and a night shot alike.
     */
    tab: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderRadius: 20,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderWidth: 1,
      borderColor: OVER_ART.panelBorder,
      backgroundColor: OVER_ART.panel,
    },
    tabTxt: {fontSize: 12, fontWeight: '700'},
    bottom: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      padding: 16,
      gap: 6,
    },
    titleInput: {
      color: c.text,
      fontSize: 25,
      fontWeight: '900',
      lineHeight: 31,
      paddingVertical: 2,
    },
    /**
     * Over art the title sits on the creator's own picture, so it takes the scrim
     * treatment every cover uses: white ink with a soft shadow. Without art there
     * is nothing to fight, and it is ordinary studio ink on the studio's surface.
     */
    titleOnArt: {
      color: c.onCanvas,
      textShadowColor: OVER_ART.inkShadow,
      textShadowOffset: {width: 0, height: 1},
      textShadowRadius: 8,
    },
    descInput: {
      color: c.textMuted,
      fontSize: 14,
      lineHeight: 20,
      paddingVertical: 2,
    },
    descOnArt: {
      color: OVER_ART.inkStrong,
      textShadowColor: OVER_ART.inkShadow,
      textShadowOffset: {width: 0, height: 1},
      textShadowRadius: 8,
    },
    reqTxt: {
      color: OVER_ART.inkMuted,
      fontSize: 12.5,
      fontWeight: '600',
      textAlign: 'center',
      marginBottom: 8,
    },
    continueBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      borderRadius: 16,
      paddingVertical: 14,
      marginTop: 4,
    },
    continueTxt: {fontSize: 16, fontWeight: '800'},
  })

/** The stage's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
